<template>
    <div
        ref="container"
        class="court"
        :class="{ 'court--made': made, 'court--missed': made === false }"
        @click.stop="onClick"
        @mousemove="onMove"
        @mouseleave="hover = null"
    >
        <!-- the court of the site, drawn on the lines of the old image (550 x 550): the stored positions mean the same -->
        <svg class="court__floor" viewBox="0 0 550 550" preserveAspectRatio="none" aria-hidden="true">
            <defs>
                <linearGradient id="cms-court-floor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#2e2e2e" />
                    <stop offset="100%" stop-color="#202020" />
                </linearGradient>
                <clipPath id="cms-court-inside">
                    <rect x="14.5" y="12.5" width="520.5" height="521" />
                </clipPath>
            </defs>

            <rect width="550" height="550" fill="#1b1b1b" />
            <rect x="14.5" y="12.5" width="520.5" height="521" fill="url(#cms-court-floor)" />

            <g clip-path="url(#cms-court-inside)">
                <circle cx="274.5" cy="-3" r="238.7" fill="rgba(3, 229, 183, 0.05)" />
                <rect x="223" y="12.5" width="104" height="146" fill="rgba(3, 229, 183, 0.14)" />
                <image :href="logo" x="215" y="462" width="120" height="68" opacity="0.22" preserveAspectRatio="xMidYMid meet" />

                <g fill="none" stroke="rgba(255, 255, 255, 0.55)" stroke-width="2.5">
                    <circle cx="274.5" cy="-3" r="238.7" stroke="#03e5b7" stroke-opacity="0.75" :stroke-width="type ? 4 : 2.5" />
                    <line x1="223" y1="12.5" x2="223" y2="158.5" />
                    <line x1="327" y1="12.5" x2="327" y2="158.5" />
                    <line x1="223" y1="158.5" x2="327" y2="158.5" />
                    <circle cx="275" cy="158.5" r="55" />
                    <circle cx="274.5" cy="540" r="90" />
                </g>

                <!-- the part of the court that does not match the shot type picked, dimmed -->
                <path v-if="type === '2pts'" :d="outsideArc" fill="rgba(0, 0, 0, 0.55)" fill-rule="evenodd" />
                <circle v-if="type === '3pts'" :cx="basket.x * 550" :cy="basket.y * 550" :r="threePointRadius * 550" fill="rgba(0, 0, 0, 0.55)" />
            </g>

            <rect x="14.5" y="12.5" width="520.5" height="521" fill="none" stroke="rgba(255, 255, 255, 0.55)" stroke-width="2.5" />
        </svg>

        <!-- where the shot will go, under the mouse -->
        <span v-if="hover" class="court__ghost" :style="{ left: `${hover.x}px`, top: `${hover.y}px` }" />

        <div v-if="wrongZone" class="court__warning">
            <v-icon small color="white" class="mr-1">mdi-alert-circle-outline</v-icon>{{ wrongZone }}
        </div>

        <span
            v-for="(shot, index) in shots"
            :key="index"
            class="court__shot"
            :class="shot.throwStatus === 'made' ? 'court__shot--made' : 'court__shot--missed'"
            :style="getShotStyle(shot)"
        >
            {{ shot.pointsType === '3pts' ? '3' : '2' }}
        </span>
    </div>
</template>

<script>
    // older shots kept the top-left corner of a 24px mark instead of the spot of the tap
    const ICON_SIZE = 24

    export default {
        name: 'ShootingChart',
        props: {
            made: {
                type: Boolean,
                default: null
            },
            // basket: position of the hoop as fractions of container width/height (0..1)
            basket: {
                type: Object,
                default: () => ({ x: 0.5, y: 0 })
            },
            // threePointRadius: radius (fraction of container width) that defines 3pt distance
            threePointRadius: {
                type: Number,
                default: 0.43
            },
            shootingId: {
                type: String,
                required: true
            },
            // '2pts' | '3pts': the shot type the user picked; taps in the other zone are not recorded
            type: {
                type: String,
                default: null
            },
            // taps this close to the 3pt line (fraction of the court) still count for either side
            lineTolerance: {
                type: Number,
                default: 0.03
            }
        },
        data: () => ({
            shots: [],
            wrongZone: null,
            wrongZoneTimeout: null,
            hover: null,
            logo: require('@/assets/main-logo.png')
        }),
        computed: {
            // the whole court with a hole in the size of the 3pt area
            outsideArc() {
                const cx = this.basket.x * 550
                const cy = this.basket.y * 550
                const r = this.threePointRadius * 550
                return `M0 0H550V550H0Z M${cx - r} ${cy} a${r} ${r} 0 1 0 ${2 * r} 0 a${r} ${r} 0 1 0 ${-2 * r} 0Z`
            }
        },
        watch: {
            shootingId() {
                this.shots = []
            }
        },
        beforeDestroy() {
            clearTimeout(this.wrongZoneTimeout)
        },
        created() {
            this.shots = []
        },
        methods: {
            onClick(e) {
                if (this.shots.length >= 1) this.shots = [] // one shot at a time
                const rect = this.$refs.container.getBoundingClientRect()
                // the spot of the tap, as a fraction of the court: the same at any court size
                const xPercent = (e.clientX - rect.left) / rect.width
                const yPercent = (e.clientY - rect.top) / rect.height
                // determine whether shot is 2pts or 3pts based on distance to basket
                const dx = xPercent - this.basket.x
                const dy = yPercent - this.basket.y
                const distanceNorm = Math.sqrt(dx * dx + dy * dy)
                if (this.type === '2pts' && distanceNorm > this.threePointRadius + this.lineTolerance) {
                    this.showWrongZone(`That's a 3PT spot — this is a 2PT shot`)
                    return
                }
                if (this.type === '3pts' && distanceNorm < this.threePointRadius - this.lineTolerance) {
                    this.showWrongZone(`That's a 2PT spot — this is a 3PT shot`)
                    return
                }
                // the button the user pressed decides the type; the court only gives the location
                const pointsType = this.type || (distanceNorm > this.threePointRadius ? '3pts' : '2pts')
                this.$emit('setCategory', pointsType)
                this.shots.push({ x: xPercent, y: yPercent, centered: true, throwStatus: this.made ? 'made' : 'missed', pointsType })
                this.$emit('updateShots', this.shots)
            },
            onMove(e) {
                const rect = this.$refs.container.getBoundingClientRect()
                this.hover = { x: e.clientX - rect.left, y: e.clientY - rect.top }
            },
            showWrongZone(message) {
                clearTimeout(this.wrongZoneTimeout)
                this.wrongZone = message
                this.wrongZoneTimeout = setTimeout(() => {
                    this.wrongZone = null
                }, 1800)
            },
            removeShot(index) {
                this.shots.splice(index, 1)
                this.$emit('updateShots', this.shots)
            },
            // the mark centred on the spot of the tap
            getShotStyle(shot) {
                const shift = shot.centered ? 0 : ICON_SIZE / 2
                return {
                    left: `calc(${shot.x * 100}% + ${shift}px)`,
                    top: `calc(${shot.y * 100}% + ${shift}px)`
                }
            }
        }
    }
</script>

<style lang="css" scoped>
    /* the court is square: fill the available width (up to 550px) and stay square,
       so the 2pt/3pt distance calculation keeps working on any screen */
    .court {
        position: relative;
        cursor: crosshair;
        width: calc(100% - 24px);
        max-width: 550px;
        aspect-ratio: 1 / 1;
        margin-left: auto;
        margin-right: auto;
        border-radius: 12px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
        overflow: hidden;
        touch-action: manipulation;
        user-select: none;
    }

    .court__floor {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
    }

    .court__ghost {
        position: absolute;
        width: 22px;
        height: 22px;
        border: 2px dashed rgba(255, 255, 255, 0.8);
        border-radius: 50%;
        transform: translate(-50%, -50%);
        pointer-events: none;
    }

    .court--made .court__ghost {
        border-color: #03e5b7;
    }

    .court--missed .court__ghost {
        border-color: #ff5a5a;
    }

    .court__shot {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        border-radius: 50%;
        transform: translate(-50%, -50%);
        font-family: Roboto, Arial, sans-serif;
        font-size: 13px;
        font-weight: 900;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
        pointer-events: none;
        animation: court-shot-in 0.18s ease-out;
    }

    /* made: mint, missed: a red ring; the number is the points of the shot */
    .court__shot--made {
        background: #03e5b7;
        border: 2px solid #fff;
        color: #1b1b1b;
    }

    .court__shot--missed {
        background: #1b1b1b;
        border: 3px solid #ff5a5a;
        color: #ff5a5a;
    }

    @keyframes court-shot-in {
        from {
            transform: translate(-50%, -50%) scale(0.4);
            opacity: 0;
        }
    }

    .court__warning {
        position: absolute;
        left: 50%;
        bottom: 12px;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        max-width: calc(100% - 24px);
        padding: 6px 12px;
        border-radius: 14px;
        background: rgba(229, 57, 53, 0.95);
        color: #fff;
        font-size: 13px;
        font-weight: 700;
        white-space: nowrap;
        pointer-events: none;
    }
</style>
